'use client';

import type { FC } from 'react';
import {
  ColorArea,
  ColorField,
  ColorSlider,
  ColorSwatch,
  ColorSwatchPicker,
  Label,
} from '@heroui/react';
import { CellColorPicker, type CellColorPickerRootProps } from '@heroui-pro/react';
import { parseColor } from 'react-aria-components';

export const DEFAULT_COLOR_PICKER_PRESETS = [
  '#EF4444',
  '#F97316',
  '#EAB308',
  '#22C55E',
  '#06B6D4',
  '#3B82F6',
  '#8B5CF6',
  '#EC4899',
  '#F43F5E',
] as const;

/** 将 #hex、rgb() 等转为取色器可用的 hex；渐变等无法解析时返回 fallback */
export function normalizeToPickerHex(value: string, fallback = '#FFFFFF'): string {
  const raw = value.trim();
  if (!raw || raw.startsWith('linear-gradient(') || raw.startsWith('radial-gradient(')) {
    return fallback;
  }
  try {
    return parseColor(raw).toString('hex').toUpperCase();
  } catch {
    return fallback;
  }
}

export type ColorPickerFieldProps = {
  /** 当前颜色（#hex、rgb() 等）；onChange 始终回传 hex 字符串 */
  value: string;
  onChange: (hex: string) => void;
  /** 触发器左侧标签，默认「颜色」 */
  label?: string;
  /** 无障碍标签，默认与 label 相同 */
  'aria-label'?: string;
  /** 与 CellColorPicker 一致，默认 default */
  variant?: NonNullable<CellColorPickerRootProps['variant']>;
  /** 预置颜色；传 false 或空数组时隐藏预置色 */
  presets?: readonly string[] | false;
  /** 无法解析 value 时取色器显示的占位色 */
  fallbackHex?: string;
  className?: string;
};

/**
 * 基于 HeroUI Pro `CellColorPicker` 的纯色选择器：触发器 + 预置色 + 色板 + 色相滑条 + 色值输入。
 *
 * @example
 * <ColorPickerField value={fillColor} onChange={setFillColor} aria-label="填充颜色" />
 */
export const ColorPickerField: FC<ColorPickerFieldProps> = ({
  value,
  onChange,
  label = '颜色',
  'aria-label': ariaLabel,
  variant = 'default',
  presets = DEFAULT_COLOR_PICKER_PRESETS,
  fallbackHex = '#FFFFFF',
  className,
}) => {
  const presetColors = presets === false ? [] : presets;

  return (
    <CellColorPicker
      aria-label={ariaLabel ?? label}
      className={className}
      value={normalizeToPickerHex(value, fallbackHex)}
      onChange={(color) => onChange(color.toString('hex'))}
      variant={variant}
    >
      <CellColorPicker.Trigger>
        <CellColorPicker.Label>{label}</CellColorPicker.Label>
        <CellColorPicker.ValueDisplay />
        <CellColorPicker.Swatch />
      </CellColorPicker.Trigger>
      <CellColorPicker.Popover>
        {presetColors.length > 0 ? (
          <ColorSwatchPicker className="justify-center pt-2" size="xs">
            {presetColors.map((preset) => {
              const presetHex = normalizeToPickerHex(preset, '');
              if (!presetHex) return null;

              return (
                <ColorSwatchPicker.Item key={presetHex} color={presetHex}>
                  <ColorSwatchPicker.Swatch />
                </ColorSwatchPicker.Item>
              );
            })}
          </ColorSwatchPicker>
        ) : null}
        <ColorArea
          aria-label="颜色区域"
          className="max-w-full"
          colorSpace="hsb"
          xChannel="saturation"
          yChannel="brightness"
        >
          <ColorArea.Thumb />
        </ColorArea>
        <ColorSlider aria-label="色相" channel="hue" className="gap-1 px-1" colorSpace="hsb">
          <Label>色相</Label>
          <ColorSlider.Output className="text-muted" />
          <ColorSlider.Track>
            <ColorSlider.Thumb />
          </ColorSlider.Track>
        </ColorSlider>
        <ColorField aria-label="色值">
          <ColorField.Group variant="secondary">
            <ColorField.Prefix>
              <ColorSwatch size="xs" />
            </ColorField.Prefix>
            <ColorField.Input />
          </ColorField.Group>
        </ColorField>
      </CellColorPicker.Popover>
    </CellColorPicker>
  );
};
